import React, { useState } from "react";
import { motion } from "framer-motion";
import { Progress } from "../components/ui/progress";
import ShareStatsCard from "../components/ShareStatsCard";
import ShareableInfographics from "../components/ShareableInfographics";

const ShopaholicTest = ({ theme, goToInput }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Shopaholic assessment questions based on established clinical criteria
  const questions = [
    {
      id: "preoccupation",
      text: "How often do you find yourself preoccupied with shopping or thinking about your next purchase?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Almost always" }
      ]
    },
    {
      id: "browsing",
      text: "How much time do you spend browsing online stores or shopping apps each day?",
      options: [
        { value: 0, label: "Less than 30 minutes" },
        { value: 1, label: "30 minutes to 1 hour" },
        { value: 2, label: "1 to 3 hours" },
        { value: 3, label: "More than 3 hours" }
      ]
    },
    {
      id: "priority",
      text: "How often does shopping take priority over other responsibilities?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Very frequently" }
      ]
    },
    {
      id: "distress",
      text: "How much distress or anxiety do you feel when unable to shop?",
      options: [
        { value: 0, label: "None" },
        { value: 1, label: "Slight" },
        { value: 2, label: "Moderate" },
        { value: 3, label: "Severe" }
      ]
    },
    {
      id: "thoughts",
      text: "How often do you think about shopping or buying things?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Almost constantly" }
      ]
    },
    {
      id: "emotional",
      text: "How often do you shop or buy things to improve your mood when feeling down, anxious, or stressed?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Almost always" }
      ]
    },
    {
      id: "control",
      text: "How often have you tried to cut back on shopping but couldn't?",
      options: [
        { value: 0, label: "Never tried / successfully cut back" },
        { value: 1, label: "Once or twice without success" },
        { value: 2, label: "Several unsuccessful attempts" },
        { value: 3, label: "Many failed attempts" }
      ]
    },
    {
      id: "financial",
      text: "How often has your shopping resulted in financial difficulties (debt, maxed credit cards, etc.)?",
      options: [
        { value: 0, label: "Never" },
        { value: 1, label: "Rarely" },
        { value: 2, label: "Sometimes" },
        { value: 3, label: "Frequently" }
      ]
    },
    {
      id: "hiding",
      text: "How often do you hide purchases or lie about how much you've spent?",
      options: [
        { value: 0, label: "Never" },
        { value: 1, label: "Rarely" },
        { value: 2, label: "Sometimes" },
        { value: 3, label: "Frequently" }
      ]
    },
    {
      id: "relationships",
      text: "How often has your shopping caused problems in your relationships?",
      options: [
        { value: 0, label: "Never" },
        { value: 1, label: "Rarely" },
        { value: 2, label: "Sometimes" },
        { value: 3, label: "Frequently" }
      ]
    },
    {
      id: "unused",
      text: "How many unused items with tags still attached do you own?",
      options: [
        { value: 0, label: "None or very few" },
        { value: 1, label: "A few" },
        { value: 2, label: "Several" },
        { value: 3, label: "Many" }
      ]
    },
    {
      id: "euphoria",
      text: "How often do you experience a 'high' or rush of excitement when making purchases?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Almost always" }
      ]
    },
    {
      id: "guilt",
      text: "How often do you feel guilt, shame, or regret after shopping?",
      options: [
        { value: 0, label: "Rarely or never" },
        { value: 1, label: "Occasionally" },
        { value: 2, label: "Frequently" },
        { value: 3, label: "Almost always" }
      ]
    }
  ];
  
  // Handle option selection
  const handleSelect = (value) => {
    const updatedAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(updatedAnswers);
    
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate results
      calculateResults(updatedAnswers);
    }
  };
  
  // Calculate assessment results
  const calculateResults = (finalAnswers) => {
    const totalScore = Object.values(finalAnswers).reduce((sum, value) => sum + value, 0);
    const maxPossibleScore = questions.length * 3;
    const scorePercentage = Math.round((totalScore / maxPossibleScore) * 100);
    
    // Determine risk level and recommendations
    let riskLevel, recommendations, description, resultEmoji;
    
    if (scorePercentage <= 25) {
      riskLevel = "Low Risk";
      description = "Your shopping habits appear to be within normal ranges.";
      resultEmoji = "✅";
      recommendations = [
        "Continue to practice mindful spending",
        "Track occasional impulse purchases with Guilt Gauge",
        "Consider setting up a monthly budget for discretionary spending"
      ];
    } else if (scorePercentage <= 50) {
      riskLevel = "Moderate Risk";
      description = "You show some signs of problematic shopping behaviors.";
      resultEmoji = "🤔";
      recommendations = [
        "Start tracking all purchases with Guilt Gauge",
        "Identify emotional triggers for shopping",
        "Implement a 24-hour waiting period for non-essential purchases",
        "Consider sharing your concerns with a trusted friend"
      ];
    } else if (scorePercentage <= 75) {
      riskLevel = "High Risk";
      description = "Your results indicate possible shopping addiction.";
      resultEmoji = "⚠️";
      recommendations = [
        "Use Guilt Gauge to thoroughly track and analyze your spending patterns",
        "Remove shopping apps from your phone",
        "Consider seeking help from a mental health professional",
        "Look into support groups for shopping addiction",
        "Create strict spending limits and accountability systems"
      ];
    } else {
      riskLevel = "Severe Risk";
      description = "Your responses strongly suggest compulsive buying disorder.";
      resultEmoji = "🚨";
      recommendations = [
        "We recommend speaking with a mental health professional",
        "Use Guilt Gauge as part of a comprehensive treatment plan",
        "Consider financial counseling for debt management",
        "Ask someone you trust to help you limit shopping access",
        "Create an emergency response plan for shopping urges"
      ];
    }
    
    setResultData({
      score: totalScore,
      maxScore: maxPossibleScore,
      percentage: scorePercentage,
      riskLevel,
      description,
      recommendations,
      resultEmoji
    });
    
    setShowResults(true);
  };
  
  // Go back to previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  // Restart the assessment
  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResultData(null);
  };
  
  // Prepare data for ShareStatsCard component
  const prepareShareData = () => {
    if (!resultData) return null;
    
    return {
      quizTitle: "Shopping Addiction Assessment",
      result: resultData.riskLevel,
      resultEmoji: resultData.resultEmoji,
      resultDescription: resultData.description,
      resultTagline: "Understanding your shopping habits is the first step to change!",
      quizType: "shopping_addiction"
    };
  };
  
  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {!showResults ? (
        <>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-3 dark:text-white">Shopping Addiction Assessment</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Answer honestly to get an accurate assessment of your shopping habits.
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span>{Math.round((currentQuestion / questions.length) * 100)}% Complete</span>
            </div>
            <Progress
              value={(currentQuestion / questions.length) * 100}
              className="h-2 bg-gray-200 dark:bg-gray-700"
              style={{
                "--progress-indicator-color": 
                  theme === "mint" ? "#10b981" : 
                  theme === "pink" ? "#ec4899" : 
                  "#8b5cf6" // lavender
              }}
            />
          </div>
          
          {/* Current question */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {questions[currentQuestion].text}
            </h2>
            
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full py-3 px-4 text-left rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                    answers[questions[currentQuestion].id] === option.value
                      ? `border-2 border-${theme}-500 dark:border-${theme}-400`
                      : ""
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <motion.button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 ${
                currentQuestion === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              whileHover={currentQuestion !== 0 ? { scale: 1.05 } : {}}
              whileTap={currentQuestion !== 0 ? { scale: 0.95 } : {}}
            >
              Previous
            </motion.button>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 self-center">
              Your results are private and stored only on your device.
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 dark:text-white">Your Shopping Addiction Assessment</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Based on established clinical criteria for compulsive buying disorder.
            </p>
          </div>
          
          {/* Results */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Your Score</div>
                <div className="text-3xl font-bold dark:text-white">
                  {resultData.score} / {resultData.maxScore}
                </div>
              </div>
              <div className={`text-lg font-semibold px-4 py-2 rounded-full ${
                resultData.riskLevel === "Low Risk" 
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : resultData.riskLevel === "Moderate Risk"
                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                  : resultData.riskLevel === "High Risk"
                  ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
              }`}>
                {resultData.riskLevel}
              </div>
            </div>
            
            <div className="mb-4">
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    resultData.percentage < 33
                      ? "bg-green-500"
                      : resultData.percentage < 66
                      ? "bg-yellow-500"
                      : "bg-red-500"
                  }`}
                  style={{ width: `${resultData.percentage}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs mt-1 text-gray-600 dark:text-gray-400">
                <span>Low Risk</span>
                <span>Moderate Risk</span>
                <span>High Risk</span>
              </div>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {resultData.description}
            </p>
            
            <div>
              <h3 className="font-semibold mb-2 dark:text-white">Recommendations:</h3>
              <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                {resultData.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-sm mr-2">•</span> {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Share Results Card */}
          <ShareStatsCard 
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.riskLevel}
            cardType="quiz"
          />

          <ShareableInfographics
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryType}
            cardType="quiz"
          />
          
          {/* Disclaimer */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6 text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Important Note:</p>
            <p>
              This assessment is for informational purposes only and not a diagnostic tool. 
              If you're concerned about your shopping habits, please consult with a qualified mental health professional.
            </p>
          </div>

          {/* Shop Recommendations */}
          {showResults && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 dark:text-white flex items-center">
                <span className="mr-2">🛍️</span> Recovery-Focused Resources
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {resultData.riskLevel === "Low Risk" ? 
                  "Maintain your healthy shopping habits with our curated selection of mindful spending tools and resources." :
                  resultData.riskLevel === "Moderate Risk" ?
                  "We've identified tools and alternatives that can help reinforce healthier shopping patterns and reduce impulse purchases." :
                  "Discover resources specifically designed to support recovery from shopping addiction and establish healthier relationships with spending."
                }
              </p>
              <motion.button
                onClick={() => window.location.href = '/shop'}
                className={`px-4 py-2 w-full rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View Supportive Resources
              </motion.button>
            </div>
          )}          
          
          {/* Action buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.button
              onClick={handleRestart}
              className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Take Test Again
            </motion.button>
            
            <motion.button
              onClick={goToInput}
              className={`py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Tracking Purchases
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ShopaholicTest;
